import React, { useEffect, useState, useCallback, useContext } from 'react';
import { AuthContext } from '../../helpers/AuthContext';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  Container,
  Tooltip,
  Chip,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import {
  fetchClientProofBundle,
  setClientProofApproval,
  fetchClientProofBundleByOrder,
} from '../../services/purchaseOrder';
import {
  isBadId,
  normalizeRow,
  isRenderableRow,
  isPdfUrl,
  isValidEmail,
  getStoredEmail,
  setStoredEmail,
} from '../proofs/proofRowUtils';
import PdfViewerDialog from '../proofs/PdfViewerDialog';
import ProofReviewCard from '../proofs/ProofReviewCard';
import ProofHeader from './Header';
import styles from './index.module.css';

const safe = (v) => v ?? '';

export default function GeneratedProofs() {
  const { poId, orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [mode, setMode] = useState(orderId ? 'order' : 'po');

  const { authState } = useContext(AuthContext) || {};
  const isInternal = authState?.status === true || (authState?.role && authState.role !== 'unknown');
  const internalUserId = Number.isFinite(Number(authState?.id)) ? Number(authState.id) : null;
  const internalEmail = authState?.email || '';
  const internalName = authState?.first_name || '';

  const [po, setPo] = useState(null);
  const [rows, setRows] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [comments, setComments] = useState({});
  const [clientEmailByProof, setClientEmailByProof] = useState({});

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfSrc, setPdfSrc] = useState('');
  const [pdfName, setPdfName] = useState('');

  const openPdf = useCallback((src, name) => {
    setPdfSrc(src);
    setPdfName(name || (src ? src.split('/').pop() : ''));
    setPdfOpen(true);
  }, []);

  const seedClientEmails = useCallback((nextBundles, nextRows) => {
    if (isInternal) return;
    const seed = getStoredEmail();
    if (!seed) return;

    const map = {};
    if (Array.isArray(nextBundles) && nextBundles.length) {
      nextBundles.forEach((bundle) => (bundle?.rows || []).forEach((row) => {
        const pid = row?.proof?.id != null ? String(row.proof.id) : null;
        if (pid) map[pid] = seed;
      }));
    }
    if (Array.isArray(nextRows) && nextRows.length) {
      nextRows.forEach((row) => {
        const pid = row?.proof?.id != null ? String(row.proof.id) : null;
        if (pid) map[pid] = seed;
      });
    }
    if (Object.keys(map).length) {
      setClientEmailByProof((prev) => ({ ...map, ...prev }));
    }
  }, [isInternal]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      if (mode === 'order') {
        const response = await fetchClientProofBundleByOrder(orderId);
        const nextBundles = Array.isArray(response?.bundles) ? response.bundles : [];
        const cleanBundles = nextBundles.map((bundle) => {
          const cleanRows = (bundle?.rows || [])
            .map(normalizeRow)
            .filter(isRenderableRow);
          return { ...bundle, rows: cleanRows };
        });
        setBundles(cleanBundles);
        setPo(null);
        setRows([]);
        seedClientEmails(cleanBundles, []);
      } else {
        const response = await fetchClientProofBundle(poId);
        const nextRows = (Array.isArray(response.rows) ? response.rows : [])
          .map(normalizeRow)
          .filter(isRenderableRow);
        setPo(response.po || null);
        setRows(nextRows);
        setBundles([]);
        seedClientEmails([], nextRows);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load proofs.');
    } finally {
      setLoading(false);
    }
  }, [mode, orderId, poId, seedClientEmails]);

  useEffect(() => {
    setMode(orderId ? 'order' : 'po');
  }, [orderId, poId]);

  useEffect(() => {
    load();
  }, [load]);

  const onChangeComment = useCallback((pid, value) => {
    setComments((prev) => (prev[pid] === value ? prev : { ...prev, [pid]: value }));
  }, []);

  const onChangeClientEmail = useCallback((pid, value) => {
    setClientEmailByProof((prev) => (prev[pid] === value ? prev : { ...prev, [pid]: value }));
  }, []);

  const submitDecision = useCallback(async (currentPoId, proofId, state) => {
    try {
      if (!proofId || isBadId(proofId)) {
        toast.error('Invalid proof. Please refresh the page.');
        setSubmitting(null);
        return;
      }

      setSubmitting(proofId);

      const comment = (comments[proofId] || '').trim();
      if (state === 'rejected' && !comment) {
        toast.error('Please add a comment before requesting changes.');
        setSubmitting(null);
        return;
      }

      const by = isInternal
        ? { name: internalName || 'Internal User', email: internalEmail || '' }
        : { name: 'Client', email: (clientEmailByProof[proofId] || '').trim() };

      const payload = {
        state,
        comments: comment,
        by,
        actor: isInternal
          ? {
              type: 'internal',
              userId: internalUserId ?? null,
              name: internalName || null,
              email: internalEmail || null,
            }
          : {
              type: 'client',
              email: by.email,
            },
      };

      if (!isInternal && !isValidEmail(payload.actor.email)) {
        toast.error('Please enter a valid email to record this approval.');
        setSubmitting(null);
        return;
      }

      await setClientProofApproval(currentPoId, proofId, payload);

      if (!isInternal && isValidEmail(by.email)) setStoredEmail(by.email);

      toast.success(state === 'approved' ? 'Approved — thank you!' : 'Sent back for changes.');
      setComments((prev) => ({ ...prev, [proofId]: '' }));
      await load();
    } catch (error) {
      console.error(error);
      toast.error('Failed to submit your decision.');
    } finally {
      setSubmitting(null);
    }
  }, [comments, load, isInternal, internalUserId, internalName, internalEmail, clientEmailByProof]);

  if (loading) {
    return (
      <Box p={6} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (mode === 'order') {
    if (!bundles.length) {
      return (
        <Box p={6} textAlign="center">
          <Typography variant="h6">No purchase orders found for this project.</Typography>
        </Box>
      );
    }

    const totalRows = bundles.reduce((sum, bundle) => sum + (bundle.rows?.length || 0), 0);
    const approvedRows = bundles.reduce(
      (sum, bundle) =>
        sum + (bundle.rows || []).filter((row) => (row.proof?.status || '').toLowerCase() === 'approved').length,
      0
    );

    return (
      <>
        <ToastContainer />
        <Box className={styles.rootContainer} sx={{ width: '100%' }}>
          <Container maxWidth="lg">
            <ProofHeader
              title="Proof Review"
              meta={[`Project #${safe(orderId)} • ${bundles.length} PO${bundles.length > 1 ? 's' : ''}`]}
              rightChips={[`${approvedRows}/${totalRows} approved`]}
            />
            <Divider sx={{ mb: 3 }} />

            <Box className={styles.proofContainer}>
              {bundles.map(({ po: bundlePo, rows: bundleRows }) => {
                const perPoApproved = (bundleRows || []).filter(
                  (row) => (row.proof?.status || '').toLowerCase() === 'approved'
                ).length;

                return (
                  <Grid container key={bundlePo.id} className={styles.proofCard}>
                    <Grid item xs={12}>
                      <Box className={styles.proofPoHeader}>
                        {isInternal && bundlePo?.supplierInfo?.supplierName && (
                          <>
                            <Typography component="h6" className={styles.proofPoHeaderTitle}>
                              {bundlePo.poNumber}
                              {bundlePo?.supplierInfo?.supplierName ? ` • ${bundlePo.supplierInfo.supplierName}` : ''}
                            </Typography>
                            <Tooltip arrow title="Internal-only: supplier line visible to logged-in staff">
                              <PersonOutlineIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: '-2px', opacity: 0.8 }} />
                            </Tooltip>
                          </>
                        )}
                        <Box className={styles.proofPoHeaderRight}>
                          <Chip size="small" label={`${perPoApproved}/${bundleRows?.length || 0} approved`} variant="outlined" />
                        </Box>
                      </Box>
                      <div className={styles.proofPoHeaderSpacer} />
                    </Grid>

                    {(bundleRows || []).map((row) => (
                      <ProofReviewCard
                        key={`po-${bundlePo.id}-proof-${String(row?.proof?.id)}`}
                        currentPoId={bundlePo.id}
                        row={row}
                        commentValue={comments[String(row?.proof?.id)] || ''}
                        onChangeComment={onChangeComment}
                        onChangeClientEmail={onChangeClientEmail}
                        clientEmail={clientEmailByProof[String(row?.proof?.id)] || ''}
                        isInternal={isInternal}
                        internalEmail={internalEmail}
                        isSubmitting={submitting === String(row?.proof?.id)}
                        onSubmitDecision={submitDecision}
                        onOpenPdf={openPdf}
                        gridClassName={styles.proofGrid}
                      />
                    ))}
                  </Grid>
                );
              })}
            </Box>
          </Container>
        </Box>

        {isPdfUrl(pdfSrc) && (
          <PdfViewerDialog open={pdfOpen} src={pdfSrc} fileName={pdfName} onClose={() => setPdfOpen(false)} />
        )}
      </>
    );
  }

  if (!po) {
    return (
      <Box p={6} textAlign="center">
        <Typography variant="h6">No purchase order found.</Typography>
      </Box>
    );
  }

  return (
    <>
      <ToastContainer />
      <Box className={styles.rootContainer} sx={{ width: '100%' }}>
        <Container maxWidth="lg">
          <ProofHeader
            title="Proof Review"
            meta={[
              `PO ${po.poNumber}`,
              isInternal ? safe(po.supplier?.Name || po.supplierInfo?.supplierName) : 'Please review below',
            ]}
            rightChips={[`${rows.filter((row) => (row.proof?.status || '').toLowerCase() === 'approved').length}/${rows.length} approved`]}
          />
          <Divider sx={{ mb: 3 }} />

          <Box className={styles.proofContainer}>
            <Grid container spacing={2} className={styles.proofCard}>
              {rows.map((row) => (
                <ProofReviewCard
                  key={`po-${po.id}-proof-${String(row?.proof?.id)}`}
                  currentPoId={po.id}
                  row={row}
                  commentValue={comments[String(row?.proof?.id)] || ''}
                  onChangeComment={onChangeComment}
                  onChangeClientEmail={onChangeClientEmail}
                  clientEmail={clientEmailByProof[String(row?.proof?.id)] || ''}
                  isInternal={isInternal}
                  internalEmail={internalEmail}
                  isSubmitting={submitting === String(row?.proof?.id)}
                  onSubmitDecision={submitDecision}
                  onOpenPdf={openPdf}
                  gridClassName={styles.proofGrid}
                />
              ))}
            </Grid>
          </Box>

          <Box mt={4} textAlign="center" color="text.secondary">
            <Typography variant="caption">
              Need help? Reply to the email you received with this link.
            </Typography>
          </Box>
        </Container>
      </Box>

      {isPdfUrl(pdfSrc) && (
        <PdfViewerDialog open={pdfOpen} src={pdfSrc} fileName={pdfName} onClose={() => setPdfOpen(false)} />
      )}
    </>
  );
}
